import React, { useState, useEffect } from "react";
import { Search, User, Loader2, ArrowLeft, MessageSquare, UserPlus, Check, Clock } from "lucide-react";
import httpClient from "../Api/axios";
import webSocketService from "../Api/webSocketService";

const CommentsPage = () => {
    const currentUserId = localStorage.getItem("userId");
    
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Selected User State
    const [selectedUser, setSelectedUser] = useState(null);
    
    // Connection Data States
    const [realConnectionCount, setRealConnectionCount] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState("Connect"); // Connect, Pending, Connected
    const [isActionLoading, setIsActionLoading] = useState(false);

    // ✅ WebSocket listener on component mount
    useEffect(() => {
        if (!currentUserId) {
            console.warn("⚠️ User ID not found in localStorage");
            return;
        }

        console.log("🔗 Connecting to WebSocket for user:", currentUserId);
        
        // Connect to WebSocket for real-time updates
        webSocketService.connect(currentUserId, (notification) => {
            console.log("📩 Real-time notification received in CommentsPage:", notification);
            // Could trigger UI updates here if needed
            // For example, refresh connection status if it changed
        });

        return () => {
            // Cleanup on unmount
            webSocketService.disconnect();
        };
    }, [currentUserId]);

    // 1️⃣ Search Logic - Debounced search for users
    useEffect(() => {
        const fetchUsers = async () => {
            if (searchQuery.trim().length < 2) {
                setSuggestions([]);
                return;
            }
            setLoading(true);
            try {
                const res = await httpClient.get(`/api/v1/signups/search?username=${searchQuery}`);
                setSuggestions(res.data || []);
            } catch (err) {
                console.error("❌ Search error:", err);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        };
        
        // Debounce search to reduce API calls
        const timer = setTimeout(fetchUsers, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // 2️⃣ Fetch connection details when a user is selected
    useEffect(() => {
        if (!selectedUser || !currentUserId) return;

        const fetchConnectionDetails = async () => {
            try {
                // Get Real Connection Count
                const countRes = await httpClient.get(`/api/v1/connections/count/${selectedUser.signUpId}`);
                const count = countRes.data?.count || 0;
                setRealConnectionCount(count);

                // Get Real Status (PENDING, ACCEPTED, NONE)
                const statusRes = await httpClient.get(
                    `/api/v1/connections/status?userA=${currentUserId}&userB=${selectedUser.signUpId}`
                );
                
                const status = statusRes.data;
                if (status === "ACCEPTED") {
                    setConnectionStatus("Connected");
                } else if (status === "PENDING") {
                    setConnectionStatus("Pending");
                } else {
                    setConnectionStatus("Connect");
                }
            } catch (err) {
                console.warn("⚠️ Failed to fetch connection details:", err.message);
                setRealConnectionCount(0);
                setConnectionStatus("Connect");
            }
        };

        fetchConnectionDetails();
    }, [selectedUser, currentUserId]);

    // 3️⃣ Handle user selection from search results
    const handleUserClick = (user) => {
        setSelectedUser(user);
        setSearchQuery("");
        setSuggestions([]);
        setConnectionStatus("Loading...");
        setRealConnectionCount(0);
    };

    // 4️⃣ Handle Connect Button Click - Send connection request
    const handleConnect = async () => {
        if (!currentUserId || !selectedUser) {
            alert("❌ User information missing. Please try again.");
            return;
        }

        setIsActionLoading(true);
        try {
            // Send connection request to backend
            const response = await httpClient.post(`/api/v1/connections/request`, {
                senderId: currentUserId,
                receiverId: selectedUser.signUpId
            });

            console.log("✅ Connection request sent successfully:", response.data);
            setConnectionStatus("Pending");
            
            // Optional: Show success message
            alert("✅ Connection request sent successfully!");
        } catch (err) {
            console.error("❌ Failed to send connection request:", err);
            
            // Handle different error cases
            if (err.response?.status === 400) {
                alert("⚠️ Request already exists or invalid user IDs");
            } else if (err.response?.status === 409) {
                alert("⚠️ You are already connected with this user");
            } else {
                alert("❌ Failed to send connection request. Please try again.");
            }
        } finally {
            setIsActionLoading(false);
        }
    };

    // 5️⃣ Back button handler
    const handleBack = () => {
        setSelectedUser(null);
        setSearchQuery("");
        setSuggestions([]);
    };

    return (
        <div style={styles.container}>
            {/* Search Header */}
            <div style={styles.searchHeader}>
                <div style={styles.searchWrapper}>
                    <Search size={20} style={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search for connections..."
                        style={styles.searchInput}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoComplete="off"
                    />
                    {loading && <Loader2 size={16} style={styles.spinner} />}
                    
                    {/* Dropdown Suggestions */}
                    {suggestions.length > 0 && (
                        <div style={styles.dropdown}>
                            {suggestions.map((u) => (
                                <div 
                                    key={u.signUpId} 
                                    style={styles.dropdownItem} 
                                    onClick={() => handleUserClick(u)}
                                >
                                    <div style={styles.userInfoMini}>
                                        <div style={styles.miniAvatar}>
                                            {u.userProfile ? (
                                                <img src={u.userProfile} style={styles.avatarImg} alt={u.userName} />
                                            ) : (
                                                <User size={14} color="#666" />
                                            )}
                                        </div>
                                        <span style={styles.userNameMini}>{u.userName}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Profile Area */}
            <div style={styles.mainArea}>
                {selectedUser ? (
                    <div style={styles.profileWrapper}>
                        {/* Back Button */}
                        <button onClick={handleBack} style={styles.backBtn}>
                            <ArrowLeft size={18} /> Back
                        </button>

                        {/* Header Layout */}
                        <div style={styles.headerLayout}>
                            {/* Profile Photo */}
                            <div style={styles.photoBox}>
                                <div style={styles.largeAvatar}>
                                    {selectedUser.userProfile ? (
                                        <img src={selectedUser.userProfile} style={styles.avatarImg} alt={selectedUser.userName} />
                                    ) : (
                                        <User size={60} color="#444" />
                                    )}
                                </div>
                            </div>

                            {/* Profile Info & Actions */}
                            <div style={styles.mainInfo}>
                                <div style={styles.nameActionRow}>
                                    <h2 style={styles.profileName}>{selectedUser.userName}</h2>
                                    <div style={styles.actionGroup}>
                                        
                                        {/* Connect Button - Dynamic Status */}
                                        <button 
                                            style={{
                                                ...styles.connectBtn,
                                                backgroundColor: 
                                                    connectionStatus === "Connected" 
                                                        ? "#057642" 
                                                        : connectionStatus === "Pending" 
                                                        ? "#666" 
                                                        : "#0a66c2",
                                                cursor: (connectionStatus === "Connected" || connectionStatus === "Pending") 
                                                    ? "default" 
                                                    : "pointer"
                                            }} 
                                            onClick={handleConnect}
                                            disabled={connectionStatus !== "Connect" || isActionLoading}
                                            title={connectionStatus === "Connected" ? "Already connected" : ""}
                                        >
                                            {isActionLoading ? (
                                                <Loader2 size={16} style={{animation: 'spin 1s linear infinite'}} />
                                            ) : (
                                                <>
                                                    {connectionStatus === "Connect" && <UserPlus size={16} />}
                                                    {connectionStatus === "Connected" && <Check size={16} />}
                                                    {connectionStatus === "Pending" && <Clock size={16} />}
                                                    <span style={{marginLeft: '6px'}}>{connectionStatus}</span>
                                                </>
                                            )}
                                        </button>

                                        {/* Message Button */}
                                        <button style={styles.messageBtn}>
                                            <MessageSquare size={16} /> Message
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Connection Count */}
                                <p style={styles.connectionText}>
                                    <strong>Connections</strong> {realConnectionCount}
                                </p>
                            </div>
                        </div>

                        {/* Separator */}
                        <div style={styles.separator}></div>

                        {/* Details Section */}
                        <div style={styles.detailsContent}>
                            <div style={styles.section}>
                                <h3 style={styles.sectionHeading}>About</h3>
                                <p style={styles.aboutText}>
                                    {selectedUser.about || "No information provided"}
                                </p>
                            </div>

                            {/* Info Grid */}
                            <div style={styles.infoGrid}>
                                <div style={styles.infoBlock}>
                                    <label style={styles.infoLabel}>Current Role</label>
                                    <span style={styles.infoValue}>{selectedUser.userRole || "Not specified"}</span>
                                </div>
                                <div style={styles.infoBlock}>
                                    <label style={styles.infoLabel}>Email Address</label>
                                    <span style={styles.infoValue}>{selectedUser.userEmail || "Not provided"}</span>
                                </div>
                                <div style={styles.infoBlock}>
                                    <label style={styles.infoLabel}>Location</label>
                                    <span style={styles.infoValue}>{selectedUser.countryRegion || "Not Specified"}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={styles.emptyState}>
                        <p>🔍 Find and grow your professional network</p>
                    </div>
                )}
            </div>
            
            <style>{`
                @keyframes spin { 
                    from { transform: rotate(0deg); } 
                    to { transform: rotate(360deg); } 
                }
            `}</style>
        </div>
    );
};

// Inline styles
const styles = {
    container: { 
        backgroundColor: '#000000', 
        height: '100vh', 
        color: '#ffffff', 
        display: 'flex', 
        flexDirection: 'column' 
    },
    searchHeader: { 
        padding: '2rem 1rem', 
        display: 'flex', 
        justifyContent: 'center',
        borderBottom: '1px solid #222'
    },
    searchWrapper: { 
        position: 'relative', 
        width: '40%',
        minWidth: '300px'
    },
    searchInput: { 
        width: '100%', 
        backgroundColor: '#121212', 
        border: '1px solid #333', 
        borderRadius: '4px', 
        padding: '0.6rem 1rem 0.6rem 3rem', 
        color: '#fff', 
        outline: 'none',
        fontSize: '1rem',
        transition: '0.2s'
    },
    searchIcon: { 
        position: 'absolute', 
        left: '1rem', 
        top: '0.7rem', 
        color: '#888',
        pointerEvents: 'none'
    },
    spinner: { 
        position: 'absolute', 
        right: '1rem', 
        top: '0.7rem', 
        animation: 'spin 1s linear infinite' 
    },
    dropdown: { 
        position: 'absolute', 
        top: '105%', 
        left: 0, 
        right: 0, 
        backgroundColor: '#1b1f23', 
        border: '1px solid #333', 
        borderRadius: '4px', 
        zIndex: 10,
        maxHeight: '300px',
        overflowY: 'auto'
    },
    dropdownItem: { 
        padding: '0.8rem 1rem', 
        cursor: 'pointer', 
        borderBottom: '1px solid #333',
        transition: '0.2s'
    },
    userInfoMini: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px' 
    },
    miniAvatar: { 
        width: '30px', 
        height: '30px', 
        borderRadius: '50%', 
        backgroundColor: '#333', 
        overflow: 'hidden' 
    },
    avatarImg: { 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover' 
    },
    userNameMini: { 
        fontSize: '0.9rem' 
    },
    mainArea: { 
        flex: 1, 
        padding: '0 4rem', 
        overflowY: 'auto' 
    },
    profileWrapper: { 
        width: '100%', 
        maxWidth: '850px', 
        margin: '0 auto',
        paddingBottom: '2rem'
    },
    backBtn: { 
        background: 'none', 
        border: 'none', 
        color: '#70b5f9', 
        cursor: 'pointer', 
        marginBottom: '2rem', 
        marginTop: '1rem',
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        fontSize: '1rem',
        transition: '0.2s'
    },
    headerLayout: { 
        display: 'flex', 
        gap: '2.5rem', 
        alignItems: 'flex-start', 
        marginBottom: '1.5rem' 
    },
    photoBox: { 
        flexShrink: 0 
    },
    largeAvatar: { 
        width: '160px', 
        height: '160px', 
        borderRadius: '8px', 
        backgroundColor: '#222', 
        overflow: 'hidden', 
        border: '1px solid #333' 
    },
    mainInfo: { 
        flex: 1, 
        paddingTop: '10px' 
    },
    nameActionRow: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '0.5rem' 
    },
    profileName: { 
        fontSize: '2.2rem', 
        fontWeight: '600', 
        margin: 0 
    },
    connectionText: { 
        fontSize: '1.1rem', 
        color: '#eef3f8', 
        marginTop: '4px' 
    },
    actionGroup: { 
        display: 'flex', 
        gap: '12px' 
    },
    connectBtn: { 
        color: '#fff', 
        border: 'none', 
        padding: '10px 24px', 
        borderRadius: '25px', 
        fontWeight: '600', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        transition: '0.3s', 
        minWidth: '130px', 
        justifyContent: 'center' 
    },
    messageBtn: { 
        backgroundColor: 'transparent', 
        color: '#70b5f9', 
        border: '1px solid #70b5f9', 
        padding: '10px 24px', 
        borderRadius: '25px', 
        fontWeight: '600', 
        cursor: 'pointer', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        transition: '0.2s'
    },
    separator: { 
        height: '1px', 
        backgroundColor: '#f3f2f1', 
        opacity: 0.2, 
        margin: '2rem 0' 
    },
    detailsContent: { 
        textAlign: 'left' 
    },
    section: { 
        marginBottom: '2.5rem' 
    },
    sectionHeading: { 
        fontSize: '1.4rem', 
        fontWeight: '600', 
        marginBottom: '1rem' 
    },
    aboutText: { 
        color: '#eef3f8', 
        lineHeight: '1.6', 
        fontSize: '1.1rem' 
    },
    infoGrid: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(1, 1fr)', 
        gap: '1.8rem' 
    },
    infoBlock: { 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '4px' 
    },
    infoLabel: { 
        color: '#888', 
        fontSize: '0.9rem', 
        fontWeight: '500' 
    },
    infoValue: { 
        fontSize: '1.1rem', 
        color: '#fff' 
    },
    emptyState: { 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        marginTop: '10rem', 
        color: '#444',
        fontSize: '1.2rem'
    }
};

export default CommentsPage;